import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { AddButton } from "share-components/mui-wrap/button/action-button";
import { PageContentContainer } from "share-components/templates/page/page-content-container";
import { PageFilterContainer } from "share-components/templates/page/page-filter-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { WorkOrdersPermission } from "commons/enum/permission";
import { Dayjs } from "dayjs";
import { DateRange } from "share-components/atomics/date-range";
import { ProductGroupSelection } from "share-components/group-selection/product-group-selection/product-group-selection";
import { ProductGroupSelectionKey } from "share-components/group-selection/product-group-selection/use-product-group-selection";
import {
  WorkOrderPoGroupSelection,
  WorkOrderPoGroupSelectionKey,
} from "share-components/group-selection/work-order-po-group-selection/work-order-po-group-selection";
import { SearchButton } from "share-components/mui-wrap/button/search-button";
import { XSearchTextField } from "share-components/mui-wrap/text-field/x-search-text-field";
import { AddWorkOrderPo } from "../add-work-order-po/add-work-order-po";
import { WorkOrderPoTable } from "./work-order-po-table";

export const WorkOrderPoList = observer(() => {
  const {
    workOrderPoManagement: {
      openNew,
      getWorkOrdersPo,
      pagination,
      queryParams: {
        dateRangeFilter: { fromAsDayJs, toAsDayJs, setProp: setDateRangeProps },
        poDateRangeFilter: {
          fromAsDayJs: fromPODayJs,
          toAsDayJs: toPODayJs,
          setProp: setPODateRangeProps,
        },
        name,
        dealingName,
        code,
        barcode,
        purchaseOrderId,
        statusId,
        wONumber,
        wOBatchNumber,
        isCompensating,
        setProp: setQueryParamProp,
      },
    },
  } = useStores();
  const onSearch = useCallback(async () => {
    pagination.setProp("pageIndex", 0);
    await getWorkOrdersPo();
  }, [getWorkOrdersPo, pagination]);

  const fromDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("from", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const toDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("to", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const fromPODateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setPODateRangeProps("from", value ? value.toString() : null);
    },
    [setPODateRangeProps],
  );

  const toPODateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setPODateRangeProps("to", value ? value.toString() : null);
    },
    [setPODateRangeProps],
  );

  const setProductFilterProp = useCallback(
    (prop: ProductGroupSelectionKey, value: string) => {
      setQueryParamProp(prop, value);
    },
    [setQueryParamProp],
  );

  const setWorkOrderFilterProp = useCallback(
    (prop: WorkOrderPoGroupSelectionKey, value: string | boolean) => {
      setQueryParamProp(prop, value);
    },
    [setQueryParamProp],
  );

  return (
    <>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <PageFilterContainer>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <DateRange
            label={"Ngày LSX"}
            fromDate={fromAsDayJs}
            toDate={toAsDayJs}
            onFromDateChanged={fromDateChangedHandler}
            onToDateChanged={toDateChangedHandler}
          ></DateRange>

          <DateRange
            label={"Ngày Đặt Hàng"}
            fromDate={fromPODayJs}
            toDate={toPODayJs}
            onFromDateChanged={fromPODateChangedHandler}
            onToDateChanged={toPODateChangedHandler}
          ></DateRange>
        </Stack>
      </PageFilterContainer>

      <PageFilterContainer>
        <ProductGroupSelection
          dealingNameSelected={dealingName}
          nameSelected={name}
          codeSelected={code}
          setSelectionProp={setProductFilterProp}
          searchWhenRender={false}
          visibleFields={["dealingName", "name", "code"]}
        />
        <XSearchTextField
          label="Mã barcode"
          value={barcode}
          onChange={(e) => setQueryParamProp("barcode", e.currentTarget.value)}
        />
      </PageFilterContainer>

      <PageFilterContainer>
        <WorkOrderPoGroupSelection
          purchaseOrderIdSelected={purchaseOrderId}
          statusIdSelected={statusId}
          woNumberSelected={wONumber}
          woBatchNumberSelected={wOBatchNumber}
          setSelectionProp={setWorkOrderFilterProp}
          isCompensating={isCompensating}
          searchWhenRender={false}
        />
        <SearchButton asyncAction={onSearch}></SearchButton>
      </PageFilterContainer>
      <PageContentContainer>
        <WorkOrderPoTable></WorkOrderPoTable>
      </PageContentContainer>

      <>{openNew && <AddWorkOrderPo></AddWorkOrderPo>}</>
    </>
  );
});

const Header = observer(() => {
  const {
    workOrderPoManagement: {
      setProp,
      getWorkOrdersPo,
      queryParams: { includeHiddenOrder, setProp: setQueryParamProp },
    },
  } = useStores();

  const includeHiddenWorkOrderPoHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setQueryParamProp("includeHiddenOrder", checked);
      await getWorkOrdersPo();
    },
    [getWorkOrdersPo, setQueryParamProp],
  );
  return (
    <>
      <Stack>
        <Typography variant="subtitle2">Quản lý lệnh sản xuất</Typography>
      </Stack>
      <Stack direction={"row"} alignItems={"center"} spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeHiddenOrder}
              onChange={includeHiddenWorkOrderPoHandler}
            />
          }
          label="Hiển thị lệnh ẩn"
        />
        <AddButton
          accessPermission={[WorkOrdersPermission.Create]}
          onClick={() => {
            setProp("openNew", true);
          }}
        >
          Thêm mới
        </AddButton>
      </Stack>
    </>
  );
});
